/**
 * JobDetailPage Component (Refactored)
 *
 * Playground-style 3-panel layout:
 * - Left: Collapsible session sidebar (time + dot)
 * - Center: Output/browser streaming
 * - Right: Job info + event stream
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useJob, useRetryJob } from '../hooks/useJobs';
import { useSessionsByJob } from '../hooks/useSessions';
import { useProject } from '../hooks/useProjects';
import { useBatch } from '../hooks/useBatches';
import { useTraceLog } from '../hooks/useTraceLog';
import { sessionService, jobService } from '../services/api';
import { JobSessionSidebar } from '../components/jobs/JobSessionSidebar';
import { JobInfoPanel } from '../components/jobs/JobInfoPanel';
import { EventStream } from '../components/playground/EventStream';
import { JsonViewer } from '../components/ui/JsonViewer';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export const JobDetailPage: React.FC = () => {
  const { job_id } = useParams<{ job_id: string }>();
  const navigate = useNavigate();

  // State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isViewingLog, setIsViewingLog] = useState(false);

  // Hook for fetching trace logs
  const traceLog = useTraceLog();

  // Settings state (initialized from project, can be overridden per run)
  const [browserType, setBrowserType] = useState<string>('stealth');
  const [useProxy, setUseProxy] = useState<boolean>(false);
  const [model, setModel] = useState<string>('gemini-3-flash-preview');
  const [settingsInitialized, setSettingsInitialized] = useState(false);
  const [tinyctxMode, setTinyctxMode] = useState<string>('live');
  const [recordingId, setRecordingId] = useState<string>('');

  // Fetch data
  const { data: job, isLoading, error, refetch } = useJob(job_id);
  const { data: project } = useProject(job?.project_id);

  // Initialize settings from project when it loads
  useEffect(() => {
    if (project && !settingsInitialized) {
      setBrowserType(project.browser_type || 'stealth');
      setUseProxy(project.use_proxy || false);
      setModel('gemini-3-flash-preview');
      setSettingsInitialized(true);
    }
  }, [project, settingsInitialized]);
  const { data: batch } = useBatch(job?.batch_id);
  const { data: sessionsData, isLoading: sessionsLoading, refetch: refetchSessions } = useSessionsByJob(job_id);
  const sessions = sessionsData?.sessions || [];
  const retryMutation = useRetryJob();

  // Auto-select latest session, or running session if exists
  useEffect(() => {
    if (sessions.length > 0) {
      // If no session selected, select the running one or the latest
      if (!activeSessionId) {
        const runningSession = sessions.find(s => s.status === 'running');
        setActiveSessionId(runningSession?.session_id || sessions[0].session_id);
      } else {
        // If a session is selected but there's a newer running session, switch to it
        const currentSession = sessions.find(s => s.session_id === activeSessionId);
        const runningSession = sessions.find(s => s.status === 'running');

        // Switch to running session if current one is not running and there's a running one
        if (runningSession && currentSession?.status !== 'running' && runningSession.session_id !== activeSessionId) {
          setActiveSessionId(runningSession.session_id);
        }
      }
    }
  }, [sessions, activeSessionId]);

  // Poll for updates when job is running
  useEffect(() => {
    if (job?.status === 'running') {
      const interval = setInterval(async () => {
        await refetch();
        await refetchSessions();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [job?.status, refetch, refetchSessions]);

  // Get active session
  const activeSession = sessions.find(s => s.session_id === activeSessionId);

  // Derived values (must be before early returns)
  const output = activeSession?.output || job?.endpoint_result;
  const events = activeSession?.session_log || [];

  // Get the latest streaming URL from events (functionResponse.response.streaming_url)
  // or fall back to session's streaming_url
  const streamingUrl = useMemo(() => {
    // Don't show streaming URL for completed sessions with no output (canceled)
    if (activeSession?.status === 'completed' && !output) {
      return null;
    }

    // Look for the latest streaming_url in action_success events (reverse order)
    for (let i = events.length - 1; i >= 0; i--) {
      const event = events[i];
      if (event.type === 'action_success' && event.data?.response?.streaming_url) {
        return event.data.response.streaming_url;
      }
    }

    // Fall back to session's streaming_url
    return activeSession?.streaming_url;
  }, [events, activeSession?.status, activeSession?.streaming_url, output]);

  // Handlers
  const handleSaveJob = async (data: { goal?: string; website_url?: string }) => {
    if (!job_id) return;
    try {
      await jobService.update(job_id, data);
      toast.success('Job updated');
      await refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update job');
      throw error;
    }
  };

  const handleDeleteJob = async () => {
    if (!job_id) return;
    try {
      await jobService.delete(job_id);
      toast.success('Job deleted');
      navigate(-1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete job');
    }
  };

  const handleRun = async () => {
    if (!job_id) return;
    try {
      await retryMutation.mutateAsync({
        id: job_id,
        settings: {
          browser_type: browserType,
          use_proxy: useProxy,
          model: model,
          ...(tinyctxMode !== 'live' ? { tinyctx_mode: tinyctxMode } : {}),
          ...(recordingId ? { recording_id: recordingId } : {}),
        },
      });
      toast.success('Job execution started');
      setIsSidebarOpen(false); // Auto-hide sidebar

      // Refetch to get the new session
      await refetch();
      await refetchSessions();

      // Wait a bit for the session to be created, then select it
      setTimeout(async () => {
        const updated = await refetchSessions();
        if (updated.data?.sessions && updated.data.sessions.length > 0) {
          // Select the newest session (first in the list)
          setActiveSessionId(updated.data.sessions[0].session_id);
        }
      }, 500);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start job');
    }
  };

  const handleSessionSelect = (sessionId: string) => {
    setActiveSessionId(sessionId);
    // Clear log viewing state when switching sessions
    setIsViewingLog(false);
    traceLog.clearLog();
  };

  // Handle viewing logs (session or step)
  const handleViewLog = async (logType: 'session' | 'step', functionCallId?: string) => {
    console.log('handleViewLog called:', { logType, functionCallId, isViewingLog });

    // Get the session ID for log fetching (use web_agent_session_id if available)
    const logSessionId = activeSession?.web_agent_session_id || activeSessionId;

    // Toggle back to streaming/output view when clicking "view session"
    if (logType === 'session' && !functionCallId) {
      console.log('Toggling back to session view');
      setIsViewingLog(false);
      traceLog.clearLog();
      return;
    }

    if (!logSessionId) return;

    setIsViewingLog(true);

    if (logType === 'step' && functionCallId) {
      await traceLog.fetchStepLog(logSessionId, functionCallId);
    } else {
      await traceLog.fetchSessionLog(logSessionId);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleStopSession = async () => {
    if (!activeSessionId) return;

    // Close dialog immediately
    setShowStopConfirm(false);

    try {
      setIsStopping(true);
      await sessionService.stop(activeSessionId);
      toast.success('Session stopped');

      // Refresh session and job data immediately
      await refetch();
      await refetchSessions();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to stop session');
      setIsStopping(false);
    }
  };

  // Reset isStopping when session stops running
  useEffect(() => {
    if (isStopping && activeSession?.status !== 'running') {
      setIsStopping(false);
    }
  }, [isStopping, activeSession?.status]);

  // Loading/error states
  if (isLoading) {
    return (
      <div className="h-[calc(100vh-64px)] flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="h-[calc(100vh-64px)] flex justify-center items-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error?.message || 'Job not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-600 text-white rounded-md"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const isRunning = job.status === 'running';

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden bg-gray-50">
      {/* Breadcrumb Header */}
      <div className="flex-shrink-0 bg-white px-4 py-2">
        <nav className="text-xs">
          <ol className="flex items-center space-x-2">
            <li>
              <button
                onClick={() => navigate('/projects')}
                className="text-gray-500 hover:text-gray-700"
              >
                Projects
              </button>
            </li>
            {project && (
              <>
                <li className="text-gray-400">/</li>
                <li>
                  <button
                    onClick={() => navigate(`/projects/${project.id}/detail`)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {project.name}
                  </button>
                </li>
              </>
            )}
            {batch && (
              <>
                <li className="text-gray-400">/</li>
                <li>
                  <button
                    onClick={() => navigate(`/batches/${batch.batch_id}`)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {batch.name}
                  </button>
                </li>
              </>
            )}
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-medium">
              Job {job.input_id || job.job_id.substring(0, 8)}
            </li>
          </ol>
        </nav>
      </div>

      {/* Main 3-Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Sessions */}
        {isSidebarOpen ? (
        <JobSessionSidebar
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSessionSelect={handleSessionSelect}
          onToggleSidebar={toggleSidebar}
          isLoading={sessionsLoading}
        />
      ) : (
        /* Collapsed Sidebar - Icon */
        <div className="w-12 bg-white flex flex-col items-center py-3">
          <button
            onClick={toggleSidebar}
            className="p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            title="Show sidebar"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Center - Output, Browser Streaming, or Log Viewer */}
        <div className="w-full h-[35%] md:h-full md:flex-1 bg-white overflow-hidden">
          {isViewingLog ? (
            // Show log content when viewing logs
            <div className="h-full overflow-auto bg-white p-6">
              {traceLog.isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : traceLog.data ? (
                <JsonViewer data={traceLog.data} />
              ) : (
                <JsonViewer data={{ error: 'log is not available' }} />
              )}
            </div>
          ) : isStopping || (activeSession?.status === 'completed' && !output && !streamingUrl) ? (
            // Show "Session canceled by user" immediately when stopping or for completed sessions with no output
            <div className="flex items-start justify-center h-full bg-white pt-[20%]">
              <div className="text-center text-gray-400">
                <div className="text-6xl mb-4">☁️</div>
                <p className="text-sm">Session canceled by user</p>
              </div>
            </div>
          ) : isRunning && !streamingUrl && !isStopping ? (
            // Show loading while session is running but streaming URL not available yet
            <div className="flex items-start justify-center h-full bg-white pt-[20%]">
              <div className="text-center text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-sm">Initializing browser...</p>
              </div>
            </div>
          ) : output ? (
            <div className="h-full overflow-auto bg-white p-6">
              {typeof output === 'string' ? (
                <pre className="text-sm text-gray-800 whitespace-pre-wrap break-all font-mono leading-relaxed">
                  {output}
                </pre>
              ) : (
                <JsonViewer data={output} />
              )}
            </div>
          ) : streamingUrl ? (
            <iframe
              src={streamingUrl}
              className="w-full h-full border-0 pointer-events-none"
              title="Browser streaming"
              sandbox="allow-same-origin allow-scripts"
            />
          ) : (
            <div className="flex items-start justify-center h-full bg-white pt-[20%]">
              <div className="text-center text-gray-400">
                <svg
                  className="mx-auto h-12 w-12 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm">Waiting for session to start</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Job Info + Events */}
        <div className="w-full h-[65%] md:h-full md:w-96 bg-white flex flex-col overflow-hidden">
          {/* Job Info - Docked at Top */}
          <JobInfoPanel
            job={job}
            webAgentSessionId={activeSession?.web_agent_session_id}
            sessionId={activeSession?.session_id}
            browserType={browserType}
            useProxy={useProxy}
            model={model}
            onBrowserTypeChange={setBrowserType}
            onUseProxyChange={setUseProxy}
            onModelChange={setModel}
            tinyctxMode={tinyctxMode}
            recordingId={recordingId}
            onTinyctxModeChange={setTinyctxMode}
            onRecordingIdChange={setRecordingId}
            onRun={handleRun}
            onStop={() => setShowStopConfirm(true)}
            onSaveJob={handleSaveJob}
            onDeleteJob={() => setShowDeleteConfirm(true)}
            isRunning={isRunning}
            isStopping={isStopping}
          />

          {/* Stop Confirmation Dialog */}
          <ConfirmDialog
            isOpen={showStopConfirm}
            title="Stop Session"
            message="Are you sure you want to stop this running session? This action cannot be undone."
            confirmLabel="Stop"
            cancelLabel="Cancel"
            onConfirm={handleStopSession}
            onCancel={() => setShowStopConfirm(false)}
            isDestructive={true}
          />

          {/* Delete Confirmation Dialog */}
          <ConfirmDialog
            isOpen={showDeleteConfirm}
            title="Delete Job"
            message="Are you sure you want to delete this job? This will remove it and all its sessions permanently."
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onConfirm={() => { setShowDeleteConfirm(false); handleDeleteJob(); }}
            onCancel={() => setShowDeleteConfirm(false)}
            isDestructive={true}
          />

          {/* Event Stream - Scrollable */}
          <div className="flex-1 flex flex-col min-h-0">
            <EventStream
              events={events}
              startTime={activeSession?.started_at}
              endTime={activeSession?.completed_at}
              autoScroll={isRunning}
              alwaysExpanded={true}
              agentSpecId={activeSession?.agent_spec_id}
              sessionId={activeSession?.web_agent_session_id || activeSessionId || undefined}
            />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
